import { getOperatorsHttpOrigin } from "../operators";
import { throwOperatorsAuthExpired } from "../operatorsAuthSession";

export const OPERATORS_SESSION_FILE_MAX_BYTES = 100 * 1024 * 1024;
export const OPERATORS_SESSION_FILE_MAX_FILES = 5;
export const OPERATORS_SESSION_FILE_MAX_TOTAL_BYTES = 200 * 1024 * 1024;

export interface OperatorsSessionFileRecord {
  mimeType: string;
  originalName: string;
  sizeBytes: number;
  workspacePath: string;
}

interface OperatorsSessionFileUploadResponse {
  items: OperatorsSessionFileRecord[];
}

function decodeContentDispositionFilename(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const encodedMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return encodedMatch[1];
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const bareMatch = value.match(/filename=([^;]+)/i);
  return bareMatch?.[1]?.trim() ?? null;
}

function parseOperatorsErrorPayload(payload: unknown): string | null {
  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload.trim();
  }
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidate = payload as Record<string, unknown>;
  if (typeof candidate.message === "string" && candidate.message.trim().length > 0) {
    return candidate.message.trim();
  }
  if (Array.isArray(candidate.message)) {
    const combined = candidate.message.filter((entry) => typeof entry === "string").join(" ");
    return combined.trim().length > 0 ? combined.trim() : null;
  }

  return null;
}

async function readOperatorsError(response: Response, fallback: string): Promise<Error> {
  const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
  if (contentType.includes("application/json")) {
    try {
      const payload = (await response.json()) as unknown;
      return new Error(parseOperatorsErrorPayload(payload) ?? fallback);
    } catch {
      return new Error(fallback);
    }
  }

  try {
    const text = await response.text();
    return new Error(parseOperatorsErrorPayload(text) ?? fallback);
  } catch {
    return new Error(fallback);
  }
}

function fallbackDownloadFilename(workspacePath: string): string {
  const segments = workspacePath.split("/");
  return segments.at(-1) || "download";
}

export async function uploadOperatorsSessionFiles(
  sessionId: string,
  files: ReadonlyArray<File>,
): Promise<OperatorsSessionFileRecord[]> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file, file.name);
  }

  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/sessions/${sessionId}/files`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (!response.ok) {
    throw await readOperatorsError(response, "Could not upload files to the Operators session.");
  }

  const payload = (await response.json()) as Partial<OperatorsSessionFileUploadResponse>;
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function downloadOperatorsSessionFile(
  sessionId: string,
  workspacePath: string,
): Promise<void> {
  const url = new URL(`${getOperatorsHttpOrigin()}/api/v1/sessions/${sessionId}/files/download`);
  url.searchParams.set("path", workspacePath);

  const response = await fetch(url, {
    credentials: "include",
  });
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (!response.ok) {
    throw await readOperatorsError(
      response,
      "Could not download the requested file from the Operators session.",
    );
  }

  const blob = await response.blob();
  const filename =
    decodeContentDispositionFilename(response.headers.get("content-disposition")) ??
    fallbackDownloadFilename(workspacePath);
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.rel = "noreferrer";
    link.style.display = "none";
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 0);
  }
}
